import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import StudioSuiteNav from '../../components/studio/StudioSuiteNav';
import { GraduationCap, Loader2, Eye, Download, FileText, Video, PenLine } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const CATEGORY_STYLE = {
  Video: { icon: Video, badge: 'bg-[#0A0F1C] text-white' },
  Copywriting: { icon: PenLine, badge: 'bg-fuchsia-600 text-white' },
  default: { icon: FileText, badge: 'bg-[#7C3AED] text-white' },
};

function ResourceCard({ r }) {
  const style = CATEGORY_STYLE[r.category] || CATEGORY_STYLE.default;
  const Icon = style.icon;
  const download = async () => {
    try {
      const res = await api.get(`/api/studio/resources/${r.resource_id}/download`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${r.title}.${r.file_type || 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error('Could not download this resource. Please try View instead.');
    }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 flex flex-col hover:shadow-md transition-shadow" data-testid={`studio-resource-${r.resource_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#F5F3FF] flex items-center justify-center flex-shrink-0">
          <Icon size={22} className="text-[#7C3AED]" />
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${style.badge}`}>{r.category}</span>
      </div>
      <h3 className="font-bold text-[#0A0F1C] text-base leading-snug mt-3">{r.title}</h3>
      <p className="text-sm text-[#64748B] mt-1.5 flex-1">{r.description}</p>
      <div className="flex items-center gap-2 mt-4">
        <a href={r.file_url} target="_blank" rel="noopener noreferrer" data-testid={`studio-resource-view-${r.resource_id}`}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#7C3AED] text-white text-sm font-bold hover:bg-[#6D28D9] transition-colors">
          <Eye size={15} /> View
        </a>
        <button onClick={download} data-testid={`studio-resource-download-${r.resource_id}`}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] text-sm font-bold hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors">
          <Download size={15} /> Download
        </button>
        <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">{r.file_type}</span>
      </div>
    </div>
  );
}

export default function StudioResources({ embedded = false }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/studio/resources')
      .then((r) => setResources(r.data || []))
      .catch(() => toast.error('Could not load resources'))
      .finally(() => setLoading(false));
  }, []);

  const Wrap = embedded ? 'div' : AdminLayout;

  return (
    <Wrap>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="studio-resources-page">
        <StudioSuiteNav active="resources" embedded={embedded} />
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0A0F1C] flex items-center gap-2">
            <GraduationCap className="text-[#7C3AED]" size={24} /> Studio Resources
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Guides, checklists &amp; templates to help you create scroll-stopping viral content. View them in your browser or download to keep.</p>
        </div>

        {loading ? (
          <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-[#94A3B8]" size={28} /></div>
        ) : resources.length === 0 ? (
          <div className="py-20 text-center text-sm text-[#64748B]" data-testid="studio-resources-empty">No resources available yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="studio-resources-grid">
            {resources.map((r) => <ResourceCard key={r.resource_id} r={r} />)}
          </div>
        )}
      </div>
    </Wrap>
  );
}
